import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Stack,
  Divider,
  Alert,
} from '@mui/material';
import {
  AccountBalanceWallet as AccountBalanceWalletIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  Paid as PaidIcon,
} from '@mui/icons-material';

const formatCurrency = (value, symbol = 'S/') => {
  const number = Number(value || 0);
  return `${symbol} ${number.toFixed(2)}`;
};

const SummaryItem = ({ title, value, icon, color = 'text.primary' }) => (
  <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5, height: '100%' }}>
    <Stack direction="row" spacing={1.2} alignItems="center" mb={1}>
      {icon}
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
    </Stack>
    <Typography variant="h6" fontWeight="bold" color={color}>
      {value}
    </Typography>
  </Paper>
);

const ValuationSummaryCard = ({ valuation }) => {
  if (!valuation) return null;

  const symbol = valuation.monsim || 'S/';
  const subtotalBase = Number(valuation.valcabsubtotalbase || valuation.valcabsubtotbase || 0);
  const subtotalAju = Number(valuation.valcabsubtotalaju || valuation.valcabsubtotaju || 0);
  const subtotal = Number(valuation.valcabsubtotal || valuation.valcabsubtot || 0);
  const impuesto = Number(valuation.valcabimp || 0);
  const total = Number(valuation.valcabtot || 0);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight="bold" mb={1}>
        Resumen económico
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2.5}>
        Totales actuales calculados por el backend considerando órdenes y ajustes activos.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <SummaryItem
            title="Subtotal base"
            value={formatCurrency(subtotalBase, symbol)}
            icon={<AccountBalanceWalletIcon color="primary" fontSize="small" />}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <SummaryItem
            title="Ajustes netos"
            value={formatCurrency(subtotalAju, symbol)}
            icon={<TrendingUpIcon color="primary" fontSize="small" />}
            color={subtotalAju >= 0 ? 'success.main' : 'error.main'}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <SummaryItem
            title="Impuesto"
            value={formatCurrency(impuesto, symbol)}
            icon={<ReceiptIcon color="primary" fontSize="small" />}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <SummaryItem
            title="Total"
            value={formatCurrency(total, symbol)}
            icon={<PaidIcon color="primary" fontSize="small" />}
            color="primary.main"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2.5 }} />

      <Alert severity="info" variant="outlined">
        El <strong>subtotal final</strong> corresponde a la suma del subtotal base más el impacto neto de los ajustes.
      </Alert>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} mt={2}>
        <div>
          <Typography variant="body2" color="text.secondary">
            Subtotal final antes de impuesto
          </Typography>
          <Typography fontWeight="bold">
            {formatCurrency(subtotal, symbol)}
          </Typography>
        </div>

        <div>
          <Typography variant="body2" color="text.secondary">
            Total final del documento
          </Typography>
          <Typography fontWeight="bold" color="primary.main">
            {formatCurrency(total, symbol)}
          </Typography>
        </div>
      </Stack>
    </Paper>
  );
};

export default ValuationSummaryCard;